// src/components/auth/AuthPages.jsx
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { formatInviteCode, isWellFormedCode, inviteCodeFromUrl } from '../../lib/invites';

// ─── Login ────────────────────────────────────────────────────────────────────
export function LoginPage({ onSwitch, onJoin }) {
  const { login, resetPassword } = useAuth();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [reset,    setReset]    = useState(false);
  const [resetSent,setResetSent]= useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); }
    catch(err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await resetPassword(email); setResetSent(true); }
    catch(err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-mark">💻</div>
          <div className="auth-logo-text">AGT<span>POS</span></div>
          <div className="auth-subtitle">Ankush Gupta Tech — Flower Shop POS</div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {reset ? (
          resetSent ? (
            <div>
              <p style={{textAlign:'center',marginBottom:16,color:'var(--text-mid)'}}>
                Check your email for a reset link.
              </p>
              <button className="btn btn-secondary" style={{width:'100%'}} onClick={()=>{setReset(false);setResetSent(false);}}>
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleReset}>
              <div className="form-group" style={{marginBottom:14}}>
                <label className="field-label">Email address</label>
                <input className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required/>
              </div>
              <button className="btn btn-primary" style={{width:'100%',marginBottom:10}} disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <button type="button" className="btn btn-ghost" style={{width:'100%'}} onClick={()=>setReset(false)}>Cancel</button>
            </form>
          )
        ) : (
          <form onSubmit={handleLogin}>
            <div className="form-group" style={{marginBottom:12}}>
              <label className="field-label">Email</label>
              <input className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus/>
            </div>
            <div className="form-group" style={{marginBottom:18}}>
              <label className="field-label">Password</label>
              <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required/>
            </div>
            <button className="btn btn-primary" style={{width:'100%',marginBottom:12}} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setReset(true)}>Forgot password?</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onSwitch}>Create shop account</button>
            </div>
            <hr className="divider" style={{margin:'14px 0 10px'}}/>
            <button type="button" className="btn btn-secondary btn-sm" style={{width:'100%'}} onClick={onJoin}>
              I have an invite code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Join an existing shop with an invite code ────────────────────────────────
// Without this, staff who "sign up" get their own empty tenant instead of
// joining the shop that invited them.
export function JoinShopPage({ onSwitch }) {
  const { joinShopWithInvite } = useAuth();
  const [form, setForm] = useState({
    code: inviteCodeFromUrl(), displayName: '', email: '', password: '', confirm: '',
  });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({
    ...f,
    [k]: k === 'code' ? formatInviteCode(e.target.value) : e.target.value,
  }));

  async function handleJoin(e) {
    e.preventDefault();
    setError('');
    if (!isWellFormedCode(form.code))     { setError('That invite code doesn\'t look right. It\'s 10 characters, like K7QF-3MRX-BD.'); return; }
    if (form.password !== form.confirm)   { setError('Passwords do not match.'); return; }
    if (form.password.length < 6)         { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try { await joinShopWithInvite(form); }
    catch (err) {
      // Auth errors carry a code; invite problems are already plain English.
      setError(err.code ? friendlyError(err.code) : err.message);
    }
    finally { setLoading(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{maxWidth:440}}>
        <div className="auth-logo">
          <div className="auth-logo-mark">💐</div>
          <div className="auth-logo-text">AGT<span>POS</span></div>
          <div className="auth-subtitle">Join your shop</div>
        </div>

        <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:14,textAlign:'center'}}>
          Enter the invite code from your manager to get access to the shop.
        </p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleJoin}>
          <div className="form-group">
            <label className="field-label">Invite code</label>
            <input
              className="input" value={form.code} onChange={set('code')}
              placeholder="K7QF-3MRX-BD" autoFocus required
              style={{fontFamily:'monospace',fontSize:16,letterSpacing:'1.5px',textTransform:'uppercase'}}
            />
          </div>
          <div className="form-group">
            <label className="field-label">Your name</label>
            <input className="input" value={form.displayName} onChange={set('displayName')} required/>
          </div>
          <div className="form-group">
            <label className="field-label">Email</label>
            <input className="input" type="email" value={form.email} onChange={set('email')} required/>
          </div>
          <div className="form-row">
            <div className="form-group flex1">
              <label className="field-label">Password</label>
              <input className="input" type="password" value={form.password} onChange={set('password')} required/>
            </div>
            <div className="form-group flex1">
              <label className="field-label">Confirm</label>
              <input className="input" type="password" value={form.confirm} onChange={set('confirm')} required/>
            </div>
          </div>
          <button className="btn btn-primary" style={{width:'100%',marginBottom:10}} disabled={loading}>
            {loading ? 'Joining…' : 'Join shop'}
          </button>
          <button type="button" className="btn btn-ghost" style={{width:'100%'}} onClick={onSwitch}>
            Back to sign in
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Register New Shop ────────────────────────────────────────────────────────
export function RegisterPage({ onSwitch }) {
  const { registerShop } = useAuth();
  const [form,    setForm]    = useState({ shopName:'', ownerName:'', email:'', phone:'', password:'', confirm:'' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (form.password.length < 6)       { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try { await registerShop(form); }
    catch(err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{maxWidth:460}}>
        <div className="auth-logo">
          <div className="auth-logo-mark">💻</div>
          <div className="auth-logo-text">AGT<span>POS</span></div>
          <div className="auth-subtitle">Set up your shop account</div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleRegister}>
          {/* Split into two labelled groups. Mixing shop details and personal
              details in one list made it unclear which was being asked for. */}
          <div className="section-label">Your business</div>
          <div className="form-group">
            <label className="field-label">Business name</label>
            <input className="input" value={form.shopName} onChange={set('shopName')} required placeholder="Rebecca's Flowers"/>
            <small style={{fontSize:11.5,color:'var(--text-muted)'}}>
              Appears on invoices, order tickets and the sidebar.
            </small>
          </div>
          <div className="form-group">
            <label className="field-label">Business phone</label>
            <input className="input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(867) 555-0100"/>
            <small style={{fontSize:11.5,color:'var(--text-muted)'}}>
              The number customers see on their invoice.
            </small>
          </div>

          <div className="section-label" style={{marginTop:18}}>Your login</div>
          <div className="form-group">
            <label className="field-label">Your name</label>
            <input className="input" value={form.ownerName} onChange={set('ownerName')} required placeholder="Rebecca Smith"/>
            <small style={{fontSize:11.5,color:'var(--text-muted)'}}>
              Recorded against the orders you take.
            </small>
          </div>
          <div className="form-group">
            <label className="field-label">Your email</label>
            <input className="input" type="email" value={form.email} onChange={set('email')} required placeholder="you@myflowers.com"/>
            <small style={{fontSize:11.5,color:'var(--text-muted)'}}>
              You'll sign in with this.
            </small>
          </div>
          <div className="form-row">
            <div className="form-group flex1">
              <label className="field-label">Password</label>
              <input className="input" type="password" value={form.password} onChange={set('password')} required/>
            </div>
            <div className="form-group flex1">
              <label className="field-label">Confirm password</label>
              <input className="input" type="password" value={form.confirm} onChange={set('confirm')} required/>
            </div>
          </div>
          <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>
            By creating an account you agree to our Terms of Service. $50/month after your 14-day free trial.
          </p>
          <button className="btn btn-primary" style={{width:'100%',marginBottom:10}} disabled={loading}>
            {loading ? 'Creating shop…' : 'Create shop account'}
          </button>
          <button type="button" className="btn btn-ghost" style={{width:'100%'}} onClick={onSwitch}>
            Already have an account? Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Complete Shop Setup ──────────────────────────────────────────────────────
// Shown when a user is signed in but has no shop/profile yet (e.g. their
// original registration was interrupted before Firestore rules were deployed).
export function CompleteSetupPage() {
  const { completeShopSetup, logout, user } = useAuth();
  const [form,    setForm]    = useState({ shopName:'', ownerName:'', phone:'' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await completeShopSetup(form); }
    catch(err) { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{maxWidth:460}}>
        <div className="auth-logo">
          <div className="auth-logo-mark">💻</div>
          <div className="auth-logo-text">AGT<span>POS</span></div>
          <div className="auth-subtitle">Finish setting up your shop</div>
        </div>

        <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:12,textAlign:'center'}}>
          Signed in as {user?.email}, but this account isn't linked to a shop yet.
        </p>

        <div style={{
          background:'var(--gold-pale)', border:'1px solid #f5d9a0', color:'var(--gold)',
          borderRadius:'var(--radius-sm)', padding:'9px 12px', fontSize:12.5, marginBottom:14,
        }}>
          <strong>Already set your shop up?</strong> Don't fill this in — it would
          create a second, empty shop. Sign out and back in, or ask for an invite
          code if you're joining someone else's shop.
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="section-label">Your business</div>
          <div className="form-group">
            <label className="field-label">Business name</label>
            <input className="input" value={form.shopName} onChange={set('shopName')} required placeholder="Rebecca's Flowers"/>
          </div>
          <div className="form-group">
            <label className="field-label">Business phone</label>
            <input className="input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(867) 555-0100"/>
          </div>
          <div className="section-label" style={{marginTop:16}}>You</div>
          <div className="form-group">
            <label className="field-label">Your name</label>
            <input className="input" value={form.ownerName} onChange={set('ownerName')} required placeholder="Rebecca Smith"/>
          </div>
          <button className="btn btn-primary" style={{width:'100%',marginBottom:10}} disabled={loading}>
            {loading ? 'Setting up…' : 'Finish setup'}
          </button>
          <button type="button" className="btn btn-ghost" style={{width:'100%'}} onClick={logout}>
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}

function friendlyError(code) {
  const map = {
    'auth/user-not-found':       'No account found with that email.',
    'auth/wrong-password':       'Incorrect password.',
    'auth/email-already-in-use': 'That email is already registered.',
    'auth/invalid-email':        'Please enter a valid email address.',
    'auth/too-many-requests':    'Too many attempts. Please try again later.',
    'auth/weak-password':        'Password must be at least 6 characters.',
  };
  return map[code] || 'Something went wrong. Please try again.';
}
